import React from 'react';

type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'outline'
    | 'ghost'
    | 'tab'
    | 'tab-active';

type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    loading?: boolean;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    fullWidth?: boolean;
    children?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    iconPosition = 'left',
    fullWidth = false,
    children,
    className = '',
    disabled,
    ...props
}) => {
    const getVariantClasses = (variant: ButtonVariant): string => {
        const variants = {
            primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
            secondary: 'bg-gray-300 hover:bg-gray-400 text-gray-700 focus:ring-gray-500',
            success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
            danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
            outline: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
            ghost: 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 focus:ring-gray-500',
            tab: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
            'tab-active': 'bg-blue-100 text-blue-700 focus:ring-blue-500'
        };
        return variants[variant];
    };

    const getSizeClasses = (size: ButtonSize): string => {
        const sizes = {
            sm: 'py-1 px-3 text-sm',
            md: 'py-2 px-4 text-sm',
            lg: 'py-3 px-4 text-sm'
        };
        return sizes[size];
    };

    const baseClasses = 'font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = getVariantClasses(variant);
    const sizeClasses = getSizeClasses(size);
    const widthClasses = fullWidth ? 'w-full' : '';
    const flexClasses = icon ? 'flex items-center justify-center gap-2' : '';

    const allClasses = [
        baseClasses,
        variantClasses,
        sizeClasses,
        widthClasses,
        flexClasses,
        className
    ].filter(Boolean).join(' ');

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {children}
                </div>
            );
        }

        // Icon-only button
        if (icon && !children) {
            return icon;
        }

        // Text-only button
        if (!icon) {
            return children;
        }

        // Button with icon and text
        return (
            <>
                {iconPosition === 'left' && icon}
                {children}
                {iconPosition === 'right' && icon}
            </>
        );
    };

    return (
        <button
            type="button"
            className={allClasses}
            disabled={disabled || loading}
            {...props}
        >
            {renderContent()}
        </button>
    );
};

export default Button; 